/* eslint max-statements: ["error", {max: 30}] -- flag-gated v1/v2 data selection + sensitive-group filtering */
/* eslint complexity: ["error", {max: 18}] -- flag-gated v1/v2 data selection + sensitive-group filtering */
/* eslint max-lines-per-function: ["error", {max: 155}] -- flag-gated v1/v2 data selection + sensitive-group filtering */
import { useEffect, useState } from "react";

import {
  GroupClassSeriesAnalyticsPageView,
  GroupClassSeriesAnalyticsSources,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { ExploreTestID, ModalTestID, NavTestID } from "@/constants/TestID";
import { router, useLocalSearchParams } from "@/crossPlatform/expo-router";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useScheduleQueryPatientId from "@/hooks/useScheduleQueryPatientId";
import { useAnalytics } from "@/services/Analytics";
import {
  useGetGroupClassesQuery,
  useGetGroupsQuery,
  useGetMyRegisteredGroupsQuery,
} from "@/store/api/schedule/groupClasses";
import {
  useAppUserProxyPatientsInTreatmentSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import GroupClassSeriesList from "@/ui/components/groupClasses/series/GroupClassSeriesList";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import UserTabSelector from "@/ui/core/UserTabSelector";
import ModalLayout from "@/ui/layout/ModalLayout";
import {
  excludeSensitiveGroups,
  shouldHideSensitiveGroups as getShouldHideSensitiveGroups,
  toAvailableGroupCardItem,
  toRegisteredGroupCardItem,
} from "@/utils/groupClassV2.util";
import { joinTestIDs } from "@/utils/string.util";
import { getTabSelectorOptions } from "@/utils/user.util";

const Modal = () => {
  const { trackPageView, trackUserSelectorEvent } = useAnalytics();
  const {
    externalId: appUserId,
    isPatient: isAppUserPatient,
    isSupport: isAppUserSupport,
  } = useAppUserSelector();
  const proxyPatientsInTreatment = useAppUserProxyPatientsInTreatmentSelector();
  // if the parent context (the Resources tab) has selected a tab
  // before entering this modal, then propagate that selected tab here
  // NOTE: we do NOT propagate the selected user tab here back to the parent context
  const { parentSelectedUserId } = useLocalSearchParams<{
    parentSelectedUserId: string;
  }>();
  const initialUserId = parentSelectedUserId || appUserId;
  const [selectedUserId, setSelectedUserId] = useState<string>(initialUserId);
  const patientId = selectedUserId;

  useEffect(() => {
    trackPageView(GroupClassSeriesAnalyticsPageView.List);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userTabs =
    getTabSelectorOptions({
      appUserCount: 0,
      appUserId,
      isAppUserPatient,
      patients: proxyPatientsInTreatment,
      shouldIncludeSelf: true,
    }) || [];

  const selectedUserTab = userTabs.find(
    (item) => item.userId === selectedUserId,
  );
  const forWhom =
    selectedUserId === appUserId ? undefined : selectedUserTab?.userName;
  const { isGroupClassRedesignEnabled } = useFeatureFlags();
  const patientIdForQuery = useScheduleQueryPatientId(patientId);
  const {
    data: queryData,
    isFetching,
    isLoading,
    refetch,
  } = useGetGroupClassesQuery(
    { patientId: patientIdForQuery },
    // eslint-disable-next-line @typescript-eslint/naming-convention -- RTKQuery option
    { skip: isGroupClassRedesignEnabled },
  );
  const {
    data: availableGroups = [],
    isFetching: isAvailableGroupsFetching,
    isLoading: isAvailableGroupsLoading,
    refetch: refetchAvailableGroups,
  } = useGetGroupsQuery(
    { patientExternalId: patientIdForQuery },
    // eslint-disable-next-line @typescript-eslint/naming-convention -- RTKQuery option
    { skip: !isGroupClassRedesignEnabled },
  );
  const {
    data: registeredGroups = [],
    isFetching: isRegisteredGroupsFetching,
    isLoading: isRegisteredGroupsLoading,
    refetch: refetchRegisteredGroups,
  } = useGetMyRegisteredGroupsQuery(
    { patientExternalId: patientIdForQuery },
    // eslint-disable-next-line @typescript-eslint/naming-convention -- RTKQuery option
    { skip: !isGroupClassRedesignEnabled },
  );

  const data = queryData || [];
  // a user viewing another user's groups must not see their sensitive REGISTRATIONS — that
  // user manages those on their own account. Available (searchable) sensitive groups still
  // show: they're only discoverable, not a private registration.
  const shouldHideSensitiveGroups = getShouldHideSensitiveGroups({
    appUserId,
    selectedUserId,
  });
  // registered groups (excluded from /v2/groups server-side) are shown first, then
  // the available groups the user can still register for
  const visibleRegisteredGroups = shouldHideSensitiveGroups
    ? excludeSensitiveGroups(registeredGroups)
    : registeredGroups;
  const groupCardItems = isGroupClassRedesignEnabled
    ? [
        ...visibleRegisteredGroups.map(toRegisteredGroupCardItem),
        ...availableGroups.map(toAvailableGroupCardItem),
      ]
    : undefined;
  const isV2ListLoading =
    isAvailableGroupsLoading ||
    isAvailableGroupsFetching ||
    isRegisteredGroupsLoading ||
    isRegisteredGroupsFetching;
  const isListLoading = isGroupClassRedesignEnabled
    ? isV2ListLoading
    : isLoading || isFetching;
  const shouldHideEmptyComponent = !isListLoading;
  const onRefresh = isGroupClassRedesignEnabled
    ? () => {
        void refetchAvailableGroups();
        void refetchRegisteredGroups();
      }
    : () => void refetch();

  return (
    <ModalLayout
      isListView
      renderHeader={() => {
        return (
          <ModalTitleHeader
            actionComponents={
              <UserTabSelector
                accessibilityLabel="User Tab Selector"
                appUserId={appUserId}
                initialUserId={selectedUserId}
                onUserChange={(userId: string) => {
                  setSelectedUserId(userId);
                  trackUserSelectorEvent({
                    component: ModalPath.GroupClassList,
                    selectedUser: userId,
                    source: GroupClassSeriesAnalyticsSources.SearchList,
                  });
                }}
                tabs={userTabs}
                testID={joinTestIDs(
                  ModalTestID.TitleHeader,
                  NavTestID.TabSelector,
                )}
              />
            }
            modalAccessibilityTitle="Search groups"
            onDismiss={() => router.dismiss()}
            testID={joinTestIDs(ModalTestID.TitleHeader, ExploreTestID.Groups)}
            title="Groups"
          />
        );
      }}
    >
      <GroupClassSeriesList
        forWhom={forWhom}
        groupCardItems={groupCardItems}
        groupClasses={data}
        isAppUserSupport={isAppUserSupport}
        isListLoading={isListLoading}
        onRefresh={onRefresh}
        patientId={patientId}
        shouldHideEmptyComponent={shouldHideEmptyComponent}
      />
    </ModalLayout>
  );
};

export default Modal;
